import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import axiosInstance from '../AxiosInstance';
import { filterNoteInput, NOTE_MAX, NOTE_RULES_TOOLTIP } from './CustomerNotesPanel';

export type SalesActualsNoteState = {
    text: string;
    salesActualsNoteId?: number | null;
    updatedUtc?: string | null;
    updatedByName?: string | null;
};

export type SalesActualsNoteTarget = {
    type: 'channel' | 'customer';
    channelId?: number | null;
    customerId?: number | null;
    description: string;
    note: SalesActualsNoteState;
};

export type SalesActualsNoteSaveOptions = {
    close?: boolean;
};

export type SalesActualsNoteDialogProps = {
    open: boolean;
    periodMonth: string;
    target: SalesActualsNoteTarget | null;
    onClose: () => void;
    onSaved: (note: SalesActualsNoteState | null, options?: SalesActualsNoteSaveOptions) => void;
};

type ConflictResponse = {
    message?: string;
    note?: {
        salesActualsNoteId?: number;
        text?: string;
        updatedUtc?: string;
        updatedByName?: string | null;
    };
};

function toPeriodMonthDate(periodMonth: string): string {
    return periodMonth.includes('-') && periodMonth.length >= 7
        ? `${periodMonth.slice(0, 7)}-01`
        : periodMonth;
}

function formatPeriodMonthLabel(periodMonth: string): string | null {
    const match = periodMonth.match(/^(\d{4})-(\d{2})/);
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return null;
    }

    const date = new Date(year, month - 1, 1);
    return `${date.toLocaleString('en-GB', { month: 'long' })} ${year}`;
}

function noteDialogTitle(description: string | undefined, periodMonth: string): string {
    const parts = [description, formatPeriodMonthLabel(periodMonth)].filter(Boolean);
    return parts.length > 0 ? `Note - ${parts.join(' ')}` : 'Note';
}

export function getSafeApiErrorMessage(data: unknown, fallback: string): string {
    if (typeof data === 'object' && data !== null) {
        const record = data as { message?: unknown; Message?: unknown };
        const message = record.message ?? record.Message;
        if (typeof message === 'string' && message.trim().length > 0) {
            return message.trim();
        }
    }

    if (typeof data !== 'string') {
        return fallback;
    }

    const trimmed = data.trim();
    if (trimmed.startsWith('<') || trimmed.includes('<!DOCTYPE') || /<\/html>/i.test(trimmed)) {
        return fallback;
    }

    return trimmed;
}

function parseConflictResponse(data: unknown): ConflictResponse | null {
    if (typeof data !== 'object' || data === null) {
        return null;
    }

    const record = data as {
        message?: string;
        Message?: string;
        note?: ConflictResponse['note'];
        Note?: ConflictResponse['note'];
    };

    return {
        message: record.message ?? record.Message,
        note: record.note ?? record.Note,
    };
}

function toNoteState(apiNote: ConflictResponse['note']): SalesActualsNoteState | null {
    if (!apiNote?.text) {
        return null;
    }

    return {
        text: apiNote.text,
        salesActualsNoteId: apiNote.salesActualsNoteId ?? null,
        updatedUtc: apiNote.updatedUtc ?? null,
        updatedByName: apiNote.updatedByName ?? null,
    };
}

async function copyDraftToClipboard(draft: string): Promise<boolean> {
    if (!draft || !navigator.clipboard?.writeText) {
        return false;
    }

    try {
        await navigator.clipboard.writeText(draft);
        return true;
    } catch {
        return false;
    }
}

const SalesActualsNoteDialog: React.FC<SalesActualsNoteDialogProps> = ({
    open,
    periodMonth,
    target,
    onClose,
    onSaved,
}) => {
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const dialogSessionKey = useMemo(() => {
        if (!target) {
            return null;
        }

        return `${target.type}:${target.channelId ?? ''}:${target.customerId ?? ''}:${target.description}`;
    }, [target]);

    useEffect(() => {
        if (open && target) {
            setText(target.note.text);
            setError(null);
            setInfo(null);
            setSaving(false);
        }
    }, [open, dialogSessionKey]);

    const hasExisting = Boolean(target?.note.text);
    const trimmed = text.trim();
    const canSave = trimmed.length > 0 && trimmed !== (target?.note.text ?? '');

    const handleChange = (value: string) => {
        setText(filterNoteInput(value).slice(0, NOTE_MAX));
    };

    const handleSave = async () => {
        if (!target || !canSave) return;
        setSaving(true);
        setError(null);
        setInfo(null);
        const draft = trimmed;
        try {
            const response = await axiosInstance.put('/sales-actuals-notes', {
                periodMonth: toPeriodMonthDate(periodMonth),
                type: target.type,
                channelId: target.channelId ?? null,
                customerId: target.customerId ?? null,
                text: draft,
                expectedSalesActualsNoteId: target.note.salesActualsNoteId ?? null,
                expectedUpdatedUtc: target.note.updatedUtc ?? null,
            });
            const saved = toNoteState(response.data) ?? { text: draft };
            onSaved(saved, { close: true });
        } catch (e: unknown) {
            const err = e as { response?: { status?: number; data?: unknown } };
            if (err.response?.status === 409) {
                const conflict = parseConflictResponse(err.response.data);
                const serverNote = toNoteState(conflict?.note);
                const message = getSafeApiErrorMessage(
                    conflict,
                    'This note was updated by someone else while you were editing.'
                );
                const copied = await copyDraftToClipboard(draft);
                setError(message);
                setInfo(copied
                    ? 'Your changes were copied to the clipboard so you can paste them back if needed.'
                    : 'Your changes were not copied to the clipboard. Copy them manually if you still need them.');
                if (serverNote) {
                    onSaved(serverNote, { close: false });
                    setText(serverNote.text);
                }
                return;
            }

            setError(getSafeApiErrorMessage(err.response?.data, 'Could not save note.'));
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        if (!target) return;
        setSaving(true);
        setError(null);
        setInfo(null);
        try {
            await axiosInstance.delete('/sales-actuals-notes', {
                params: {
                    periodMonth: toPeriodMonthDate(periodMonth),
                    type: target.type,
                    channelId: target.channelId ?? undefined,
                    customerId: target.customerId ?? undefined,
                },
            });
            onSaved(null, { close: true });
        } catch (e: unknown) {
            const err = e as { response?: { data?: unknown } };
            setError(getSafeApiErrorMessage(err.response?.data, 'Could not clear note.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{noteDialogTitle(target?.description, periodMonth)}</DialogTitle>
            <DialogContent>
                {(error || info) && (
                    <Alert severity={error ? 'warning' : 'info'} sx={{ mb: 2 }}>
                        {[error, info].filter(Boolean).join(' ')}
                    </Alert>
                )}
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={3}
                    margin="dense"
                    value={text}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={saving}
                    helperText={`${text.length} / ${NOTE_MAX}. ${NOTE_RULES_TOOLTIP}`}
                    inputProps={{ 'aria-label': 'Note text' }}
                />
            </DialogContent>
            <DialogActions>
                {hasExisting && (
                    <Button onClick={() => void handleClear()} disabled={saving} color="error">
                        Clear
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={() => void handleSave()}
                    disabled={saving || !canSave}
                    variant="contained"
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SalesActualsNoteDialog;
